import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Phone, Award, Users, Heart, Clock, CheckCircle, Star, ArrowRight, Play } from 'lucide-react';
import { BookingModal } from '../components/BookingModal';
import { HeroSlider } from '../components/HeroSlider';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";

export default function Home() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const services = [
    {
      title: 'BPS Denture',
      description: 'Certified BPS Denture specialist with 10+ years of clinical experience',
      icon: 'ðŸ¦·',
      highlighted: true
    },
    {
      title: 'Dental Implant',
      description: 'Permanent solution for missing teeth with natural look and feel',
      icon: 'âš•ï¸'
    },
    {
      title: 'Root Canal',
      description: 'Painless root canal treatment with advanced techniques',
      icon: 'ðŸ’Š'
    },
    {
      title: 'Dental Laminates',
      description: 'Transform your smile with custom dental laminates',
      icon: 'âœ¨'
    }
  ];

  const trustIndicators = [
    { icon: <Award className="w-8 h-8" />, value: '16+', label: 'Years Experience' },
    { icon: <Users className="w-8 h-8" />, value: '5000+', label: 'Happy Patients' },
    { icon: <Heart className="w-8 h-8" />, value: 'BDS', label: 'Qualified Dentist' },
    { icon: <CheckCircle className="w-8 h-8" />, value: 'Certified', label: 'BPS Specialist' }
  ];

  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      <HeroSlider onBookAppointment={() => setBookingOpen(true)} />

      <section className="py-20 bg-white" data-testid="trust-indicators-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {trustIndicators.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`trust-indicator-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 text-primary mb-4">
                  {item.icon}
                </div>
                <p className="text-3xl font-heading font-bold text-slate-900 mb-2">{item.value}</p>
                <p className="text-slate-600 font-medium">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-32 bg-slate-50" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-bold text-primary uppercase tracking-widest">Our Services</span>
              <h2 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 mt-4 mb-6">
                Complete Dental Care
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                All your dental needs in one place with modern technology and expert care
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                className={`group relative overflow-hidden rounded-3xl p-8 shadow-sm hover:shadow-2xl transition-all duration-500 border ${
                  service.highlighted
                    ? 'bg-gradient-to-br from-primary to-secondary text-white border-transparent'
                    : 'bg-white border-slate-100 hover:border-blue-200'
                }`}
                data-testid={`service-card-${index}`}
              >
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className={`font-heading font-bold text-xl mb-3 ${service.highlighted ? 'text-white' : 'text-slate-900'}`}>
                  {service.title}
                </h3>
                <p className={`leading-relaxed ${service.highlighted ? 'text-white/90' : 'text-slate-600'}`}>
                  {service.description}
                </p>
                {service.highlighted && (
                  <div className="mt-4 inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold">
                    Certified Specialist
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="view-all-services-button"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-primary text-primary font-semibold hover:bg-blue-50 transition-all duration-300"
            >
              View All Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-32 bg-white" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1631596577204-53ad0d6e6978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxmcmllbmRseSUyMGRlbnRpc3QlMjB0cmVhdGluZyUyMHBhdGllbnQlMjBzbWlsZSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzY4Nzc0NzU3fDA&ixlib=rb-4.1.0&q=85"
                alt="Dr. Harale treating patient"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-bold text-primary uppercase tracking-widest">Why Choose Us</span>
              <h2 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 mt-4 mb-8">
                Excellence in Dental Care
              </h2>

              <div className="space-y-6">
                {[
                  { title: 'Expert Care', desc: '16+ years of specialized dental expertise' },
                  { title: 'Modern Technology', desc: 'State-of-the-art equipment and techniques' },
                  { title: 'Patient Comfort', desc: 'Gentle, pain-free treatments in a relaxing environment' },
                  { title: 'Comprehensive Services', desc: 'Complete dental care for the whole family' }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4" data-testid={`why-choose-item-${index}`}>
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="video-testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-bold text-primary uppercase tracking-widest">Patient Stories</span>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 mt-4 mb-6">
              Real Experiences, Real Smiles
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Watch what our patients have to say about their dental journey with us
            </p>
          </motion.div>

          <Carousel className="max-w-5xl mx-auto">
            <CarouselContent>
              <CarouselItem>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video bg-slate-900">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                    title="Patient Testimonial 1"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video bg-slate-900">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                    title="Patient Testimonial 2"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video bg-slate-900">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                    title="Patient Testimonial 3"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious className="left-4" />
            <CarouselNext className="right-4" />
          </Carousel>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-primary to-secondary text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-4xl sm:text-5xl mb-6">
              Ready for Your Best Smile?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Schedule your appointment today and experience exceptional dental care
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setBookingOpen(true)}
                data-testid="cta-book-appointment-button"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-primary font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                Book Appointment
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="tel:09975727098"
                data-testid="cta-call-button"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white text-white font-semibold text-lg hover:bg-white/10 transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                099757 27098
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
}
